import {
  EditableInputCell,
  EditableInputCellEditMode,
  EditableInputCellViewMode,
} from '@src/shared/components/editable-input-cell';
import type { TCustomField } from '@src/shared/types/custom-field';
import { FIELD_TYPES } from '@src/shared/types/field';

import {
  EditablePhoneNumberCell,
  EditablePhoneNumberCellEditMode,
  EditablePhoneNumberCellViewMode,
} from '@src/shared/components/editable-phone-number-cell';

import { parseDate } from '@internationalized/date';

import {
  EditableDateCell,
  EditableDateCellEditMode,
  EditableDateCellViewMode,
} from '@src/shared/components/editable-date-cell';
import customFieldValueApi from '../services/custom-field-value-api';
import {
  EditableDropdownCell,
  EditableDropdownCellEditMode,
  EditableDropdownCellItem,
  EditableDropdownCellViewMode,
  EditableDropdownCellViewModeItem,
} from '@src/shared/components/editable-dropdown-cell';
import { useState } from 'react';
import { CustomFieldCountryCell } from './custom-field-country-cell';

export function CustomFieldCell<
  T extends { id: number; custom_fields?: TCustomField[] },
>({ entity, index }: { entity: T; index: number }) {
  const customField = entity.custom_fields?.[index];
  if (!customField) {
    return <span>-</span>;
  }

  const customFieldId = customField.id;
  const customFieldValueId = customField.value?.id;
  const customFieldValueAnswer = customField.value?.value || [];

  const { mutate: createCustomFieldValue } = customFieldValueApi.useCreate();
  const { mutate: updateCustomFieldValue } = customFieldValueApi.useUpdate();
  const { mutate: deleteCustomFieldValue } = customFieldValueApi.useDelete();

  const [answer, setAnswer] = useState(customFieldValueAnswer);
  const displayValue = answer?.[0];

  const handleValueChange = (value: string | string[]) => {
    if ((value || '') === (displayValue || '')) return;

    const valueArray = Array.isArray(value) ? value : [value];

    if (!value || !value.length) {
      if (!customFieldValueId) return;
      deleteCustomFieldValue({ id: customFieldValueId });
      setAnswer([]);
      return;
    }

    if (customFieldValueId) {
      updateCustomFieldValue({
        id: customFieldValueId,
        value: valueArray,
      });
      setAnswer(valueArray);
      return;
    }

    createCustomFieldValue({
      value: Array.isArray(value) ? value : [value],
      entity_id: entity.id,
      field: customFieldId,
    });
    setAnswer(valueArray);
  };

  switch (customField.type) {
    case FIELD_TYPES.shortAnswer:
    case FIELD_TYPES.paragraph:
      return (
        <EditableInputCell copyToText={displayValue}>
          <EditableInputCellViewMode>
            {displayValue || '-'}
          </EditableInputCellViewMode>
          <EditableInputCellEditMode
            value={displayValue}
            onBlur={(e) => handleValueChange(e.target.value)}
          />
        </EditableInputCell>
      );

    case FIELD_TYPES.number:
      return (
        <EditableInputCell>
          <EditableInputCellViewMode>
            {displayValue || '-'}
          </EditableInputCellViewMode>
          <EditableInputCellEditMode
            value={displayValue}
            type='number'
            onBlur={(e) => handleValueChange(e.target.value)}
          />
        </EditableInputCell>
      );

    case FIELD_TYPES.phoneNumber:
      return (
        <EditablePhoneNumberCell copyToText={displayValue}>
          <EditablePhoneNumberCellViewMode>
            {displayValue || '-'}
          </EditablePhoneNumberCellViewMode>
          <EditablePhoneNumberCellEditMode
            value={displayValue}
            onBlur={(value) => handleValueChange(value)}
          />
        </EditablePhoneNumberCell>
      );

    case FIELD_TYPES.checklist:
      return (
        <EditableDropdownCell>
          <EditableDropdownCellViewMode className='flex items-center'>
            {answer.length
              ? answer.map((ans) => (
                  <EditableDropdownCellViewModeItem key={ans}>
                    {ans}
                  </EditableDropdownCellViewModeItem>
                ))
              : '-'}
          </EditableDropdownCellViewMode>

          <EditableDropdownCellEditMode>
            {customField.options.map((option) => {
              const isSelected = answer?.includes(option);

              return (
                <EditableDropdownCellItem
                  type='checkbox'
                  key={option}
                  onClick={(e) => {
                    e.preventDefault();

                    if (isSelected) {
                      handleValueChange(answer?.filter((a) => a !== option));
                    } else {
                      handleValueChange([...answer, option]);
                    }
                  }}
                  isSelected={isSelected}
                >
                  {option}
                </EditableDropdownCellItem>
              );
            })}
          </EditableDropdownCellEditMode>
        </EditableDropdownCell>
      );

    case FIELD_TYPES.ratio:
    case FIELD_TYPES.dropdown:
      return (
        <EditableDropdownCell>
          <EditableDropdownCellViewMode>
            <span className='truncate'>{displayValue || '-'}</span>
          </EditableDropdownCellViewMode>

          <EditableDropdownCellEditMode>
            {customField.options.map((option) => (
              <EditableDropdownCellItem
                key={option}
                onClick={() => handleValueChange(option)}
                isSelected={option === displayValue}
              >
                {option}
              </EditableDropdownCellItem>
            ))}
          </EditableDropdownCellEditMode>
        </EditableDropdownCell>
      );

    case FIELD_TYPES.date:
      return (
        <EditableDateCell
          value={displayValue ? parseDate(displayValue) : null}
          onChange={(value) => {
            if (!value) return;
            handleValueChange(value.toString());
          }}
        >
          <EditableDateCellViewMode>
            {displayValue || '-'}
          </EditableDateCellViewMode>

          <EditableDateCellEditMode />
        </EditableDateCell>
      );

    case FIELD_TYPES.country:
      return (
        <CustomFieldCountryCell
          displayValue={displayValue}
          onValueChange={handleValueChange}
        />
      );
  }
}
